<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-dark-blue" sprite="flow" />
          Branches
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-blue" sprite="flow" />
            Branches
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ totalRecords }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
        </div>
        <div class="ml-10">
          <el-button @click="createVisible = true">Add New</el-button>
        </div>
        <div v-if="tableData.length" class="box right">
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="recordsPerPage"
            :pager-count="5"
            :total="totalRecords"
          />
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <!-- ⭐ Create Dialog ⭐ -->
    <el-dialog
      id="create-modal"
      class="form-modal"
      :visible.sync="createVisible"
      v-loading="createProcessing"
      :fullscreen="true"
      :show-close="false"
      :modal="false"
    >
      <div id="create-modal-header" class="modal-header">
        <div class="modal-header-layout flex grid-6 align-center">
          <h1 class="modal-title col-2">Create a Branch</h1>
          <div class="modal-actions col-2">
            <el-button type="primary" @click="createOperation"
              >Create</el-button
            >
            <el-button
              type="default"
              class="hidden-xs-only"
              @click="cancelOperation('create')"
              >Cancel</el-button
            >
            <el-button
              type="default"
              icon="el-icon-close"
              class="hidden-sm-and-up"
              @click="cancelOperation('create')"
            ></el-button>
          </div>
        </div>
      </div>

      <el-form>
        <el-input v-model="createForm.name" placeholder="Name">
          <template slot="prefix">Name</template>
        </el-input>
        <dads-select
          v-model="createForm.timezone"
          placeholder="Select a time zone"
          allow-create
          filterable
        >
          <template slot="prefix">Time zone</template>
          <el-option
            v-for="(timezone, index) in timezones"
            :key="index"
            :label="timezone.name"
            :value="timezone.name"
          />
        </dads-select>

        <el-checkbox v-model="createForm.default">Set as Default</el-checkbox>
      </el-form>
    </el-dialog>

    <!-- ⭐ Edit Dialog ⭐ -->
    <el-dialog
      id="edit-modal"
      class="form-modal"
      :visible.sync="editVisible"
      v-loading="editProcessing"
      :fullscreen="true"
      :show-close="false"
      :modal="false"
    >
      <div id="edit-modal-header" class="modal-header">
        <div class="modal-header-layout flex grid-6 align-center">
          <h1 class="modal-title col-2">View / Edit a Branch</h1>
          <div class="modal-actions col-2">
            <el-button type="primary" @click="editOperation">Save</el-button>
            <el-button
              type="default"
              class="hidden-xs-only"
              @click="cancelOperation('edit')"
              >Cancel</el-button
            >
            <el-button
              type="default"
              icon="el-icon-close"
              class="hidden-sm-and-up"
              @click="cancelOperation('edit')"
            ></el-button>
          </div>
        </div>
      </div>

      <el-form>
        <recent-audits service="Branches" :record="selectedBranch" />
        <el-input v-model="editForm.name" placeholder="Name">
          <template slot="prefix">Name</template>
        </el-input>
        <dads-select
          v-model="editForm.timezone"
          placeholder="Select a time zone"
          allow-create
          filterable
        >
          <template slot="prefix">Time zone</template>
          <el-option
            v-for="(timezone, index) in timezones"
            :key="index"
            :label="timezone.name"
            :value="timezone.name"
          />
        </dads-select>

        <el-checkbox v-if="isNotDefaultBranch" v-model="editForm.default"
          >Set as Default</el-checkbox
        >
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import feathersClient, { apiV2Host } from "../../../feathers/feathers-client";
import { debounce } from "lodash";
import { globalEventBus } from "@/globalEventBus";
import { ExportToCsv } from "export-to-csv";
import RecentAudits from "@/components/RecentAudits";
import DadsSelect from "@/components/select";

export default {
  name: "BranchesTable",
  components: { RecentAudits, DadsSelect },
  data() {
    return {
      eventChannel: null,
      totalRecords: 0,
      recordsPerPage: 100,
      page: 1,
      maxPage: 1,
      tableData: [],
      search: "",
      visibleActionsId: "",
      createVisible: false,
      createProcessing: false,
      tableLoading: false,
      timezones: [],
      createForm: {
        tenantId: null,
        name: "",
        timezone: "",
        default: false,
      },
      selectedBranch: null,
      editVisible: false,
      editProcessing: false,
      editForm: {
        tenantId: null,
        name: "",
        timezone: "",
        default: false,
      },
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-blue" sprite="clock" />
                </div>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          sortable: true,
        },
        {
          field: "default",
          title: "Default",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return row.default === 1 ? "Yes" : "No";
            },
          },
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  {/* Edit button */}
                  <el-tooltip placement="top" content="Edit">
                    <el-button
                      id={"edit-" + row.id}
                      icon="el-icon-edit"
                      onClick={() => this.openEditModal(row.id)}
                      class="table-action-button icon-blue"
                    />
                  </el-tooltip>

                  {/* Delete button (show only if default is 0) */}
                  {row.default === 0 && (
                    <el-tooltip placement="top" content="Delete">
                      <el-button
                        id={"delete-" + row.id}
                        icon="el-icon-delete"
                        onClick={() => this.deleteOperation(row.id)}
                        class="table-action-button icon-red"
                      />
                    </el-tooltip>
                  )}
                </div>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    user() {
      return this.$store.state.auth.user;
    },

    isNotDefaultBranch() {
      if (this.selectedBranch) {
        return this.selectedBranch.default === 0;
      }

      return false;
    },
  },
  watch: {
    selectedTenant() {
      this.findBranches();
      feathersClient
        .service("channel-memberships")
        .remove(null, { query: { channelName: this.eventChannel } });
      this.joinSocketChannel();
    },

    page: debounce(function () {
      this.findBranches();
    }, 500),

    user() {
      this.findBranches();
      if (!this.eventChannel) {
        this.joinSocketChannel();
      }
    },

    selectedBranch(val) {
      if (val) {
        const { name, timezone, default: isDefault } = val;
        this.editForm.name = name;
        this.editForm.timezone = timezone || "";
        this.editForm.default = isDefault === 1;
      }
    },

    createVisible(val) {
      if (val) {
        this.createForm.timezone = this.selectedTenant.timezone.name;
      }
    },

    search: debounce(function () {
      this.findBranches();
    }, 500),
  },
  methods: {
    ...mapActions("branches", {
      createBranch: "create",
      patchBranch: "patch",
      deleteBranch: "remove",
    }),

    getTimezoneOptions() {
      feathersClient
        .service("timezones")
        .find({
          query: {
            $sort: { name: 1 },
          },
        })
        .then((res) => (this.timezones = res));
    },

    // Pagination
    pageNext() {
      if (this.page < this.maxPage) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map((item) => ({
          name: item.name,
        }));

        const options = {
          filename: "branches",
          showLabels: true,
          headers: ["Name"],
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    async openEditModal(id) {
      try {
        const response = await fetch(`${apiV2Host}/admin/branches/${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            "Content-Type": "application/json",
          },
        });
        const res = await response.json();
        this.selectedBranch = res;
        this.editVisible = true;
      } catch (error) {
        console.error("Error fetching branch details:", error);
      }
    },

    findBranches() {
      this.tableLoading = true;
      this.tableData = [];
      let url = `${apiV2Host}/admin/branches?tenantId=${this.selectedTenant.id}&recordsPerPage=${this.recordsPerPage}&page=${this.page}`;
      if (this.search && this.search.length) {
        url += `&name=${this.search}`;
      }
      fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((res) => {
          this.tableData = [...res.records];
          this.totalRecords = res.totalRecords;
          this.maxPage = res.maxPage;
          this.tableLoading = false;
        })
        .catch((error) => {
          console.error("Error fetching branches:", error);
          this.tableLoading = false;
        });
    },

    createOperation() {
      this.$confirm("Create a Branch?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      }).then(() => {
        this.createProcessing = true;
        const createBody = { ...this.createForm };
        createBody.default = createBody.default ? 1 : 0;
        createBody.tenantId = this.selectedTenant.id;

        fetch(`${apiV2Host}/admin/branches`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(createBody),
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((errorData) => {
                throw errorData;
              });
            }
            return response.json();
          })
          .then((data) => {
            this.$notify({
              title: "Success",
              message: "Created successfully!",
              type: "success",
            });
            this.createProcessing = false;
            this.cancelOperation("create");
            this.findBranches();
          })
          .catch((error) => {
            if (
              error &&
              error.validationErrors &&
              error.validationErrors.length > 0
            ) {
              // Handle validation errors
              this.$notify({
                title: error.message || "Validation Error",
                message: error.validationErrors.join(", "),
                type: "error",
              });
            } else if (error && error.message) {
              this.$notify({
                title: error.name || "Error",
                message: error.message,
                type: "error",
              });
            } else {
              this.$notify({
                title: "Error",
                message: "An unknown error occurred",
                type: "error",
              });
            }

            this.createProcessing = false;
          });
      });
    },

    editOperation() {
      this.$confirm("Edit branch", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      }).then(() => {
        this.editProcessing = true;

        const editBody = {
          name: this.editForm.name,
          timezone: this.editForm.timezone,
          default: this.editForm.default,
        };
        editBody.default = editBody.default ? 1 : 0;

        fetch(`${apiV2Host}/admin/branches/${this.selectedBranch.id}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editBody),
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((errorData) => {
                throw errorData;
              });
            }
            return response.json();
          })
          .then((data) => {
            this.$notify({
              title: "Success",
              message: "Edited successfully!",
              type: "success",
            });
            this.editProcessing = false;
            this.cancelOperation("edit");
            this.findBranches();
          })
          .catch((error) => {
            if (
              error &&
              error.validationErrors &&
              error.validationErrors.length > 0
            ) {
              // Handle validation errors
              this.$notify({
                title: error.message || "Validation Error",
                message: error.validationErrors.join(", "),
                type: "error",
              });
            } else if (error && error.message) {
              this.$notify({
                title: error.name || "Error",
                message: error.message,
                type: "error",
              });
            } else {
              this.$notify({
                title: "Error",
                message: "An unknown error occurred",
                type: "error",
              });
            }

            this.editProcessing = false;
          });
      });
    },

    removeBranch(id) {
      this.tableLoading = true;

      fetch(`${apiV2Host}/admin/branches/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
        },
      })
        .then(() => {
          this.$notify({
            title: "Success",
            message: "Deleted successfully!",
            type: "success",
          });
          this.tableLoading = false;
          this.findBranches();
        })
        .catch((error) => {
          if (error.hasOwnProperty("errors")) {
            this.$notify({
              title: error.message,
              message: error.errors[Object.keys(error.errors)[0]],
              type: "error",
            });
          } else {
            this.$notify({
              title: error.name,
              message: error.message,
              type: "error",
            });
          }
          this.tableLoading = false;
        });
    },

    // 👨‍🔧 Need to work here 👨‍🔧 //
    async deleteOperation(id) {
      this.$confirm(
        "Any entities assigned to this branch will be reassigned to your Default Branch. Continue?",
        "Warning",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          type: "warning",
        }
      )
        .then(() => {
          this.removeBranch(id);
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
        this.createForm = {
          tenantId: null,
          name: "",
          timezone: "",
          default: false,
        };
      } else if (type === "edit") {
        this.editVisible = false;
        this.editForm = {
          tenantId: "",
          name: "",
          timezone: "",
          default: false,
        };
      }
    },

    removeListeners() {
      globalEventBus.$off("appReconnected");
    },
  },

  mounted() {
    if (this.user) {
      this.findBranches();
      this.getTimezoneOptions();
    }
  },

  filters: {
    formatNumber(value) {
      return `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(
        6,
        4
      )}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.el-form {
  max-width: 600px;
  margin: 0 auto;
}

.el-checkbox {
  margin-left: 0;
}
</style>
